'use client';

import { useEffect, useMemo, useState } from 'react';
import { X } from 'lucide-react';
import LiveWhiteboard, { type DemoSelection } from '@/components/whiteboard-v2/LiveWhiteboard';
import type { StoredProblem } from '@/components/whiteboard-v2/types';
import type { LiveModeStatus, ProblemContext } from './useLiveMode';
import { normalizeLiveTutorSubject } from '@/lib/liveTutorSubject';

interface LiveModeModalProps {
  isOpen: boolean;
  status: LiveModeStatus;
  contextTitle?: string;
  contextType?: 'question' | 'key_property' | 'mistake_review' | 'lesson_review';
  context?: ProblemContext | null;
  isModelSpeaking: boolean;
  totalTokens: number;
  promptTokens: number;
  outputTokens: number;
  onClose: () => void;
  whiteboardScript?: unknown;
  whiteboardTriggerIndex?: number;
  currentQuestionIndex?: number;
  onQuestionChange?: (index: number) => void;
  isLoadingScript?: boolean;
  notes?: unknown[];
}

function buildProblemSelection(context: ProblemContext, selectedWrongIndex: number): StoredProblem {
  const wrongPool =
    context.allAnswers?.filter((answer) => !answer.isCorrect) ??
    context.wrongAnswers ??
    [];
  const selectedWrong = wrongPool[selectedWrongIndex] ?? wrongPool[0];

  if (selectedWrong) {
    return {
      id: `mistake-${context.elementId}-${selectedWrong.questionNumber ?? 1}`,
      question: selectedWrong.question,
      answer: selectedWrong.correctAnswer,
      explanation:
        selectedWrong.explanation ||
        `The correct answer is ${selectedWrong.correctAnswer}. Your answer of ${selectedWrong.userAnswer || '(empty)'} was incorrect.`,
      createdAt: new Date().toISOString(),
      subject: normalizeLiveTutorSubject(context.subject),
      skillId: context.skillId,
      options: context.options,
      type: context.options?.length ? 'multiple_choice' : 'math_blank_filling',
      sourceType: 'problem',
    };
  }

  return {
    id: context.elementId,
    question: context.question,
    answer: '',
    explanation: '',
    createdAt: new Date().toISOString(),
    subject: normalizeLiveTutorSubject(context.subject),
    skillId: context.skillId,
    options: context.options,
    type: context.options?.length ? 'multiple_choice' : 'math_blank_filling',
    sourceType: 'problem',
  };
}

function buildSelection(context: ProblemContext, selectedWrongIndex: number): DemoSelection {
  if (context.contextType === 'key_property' && context.keyProperty) {
    return {
      kind: 'skill',
      skillId: context.skillId || context.elementId,
      keyProperty: {
        id: context.elementId,
        title: context.keyProperty.title,
        content: context.keyProperty.content,
        examples: context.keyProperty.examples,
        explanation: context.keyProperty.explanation,
      },
      problem: {
        id: `key-property-${context.elementId}`,
        question: context.question || context.keyProperty.title,
        answer: '',
        explanation: '',
        createdAt: new Date().toISOString(),
        subject: normalizeLiveTutorSubject(context.subject),
        sourceType: 'key_property',
      },
    };
  }

  return {
    kind: 'problem',
    problem: buildProblemSelection(context, selectedWrongIndex),
  };
}

export function LiveModeModal({ isOpen, context, onClose }: LiveModeModalProps) {
  const [selectedWrongIndex, setSelectedWrongIndex] = useState(0);

  const wrongAnswers = useMemo(() => {
    if (!context || context.contextType !== 'mistake_review') return [];
    if (context.allAnswers && context.allAnswers.length > 0) {
      return context.allAnswers.filter((answer) => !answer.isCorrect);
    }
    return context.wrongAnswers || [];
  }, [context]);

  useEffect(() => {
    setSelectedWrongIndex(0);
  }, [context?.elementId, context?.contextType, wrongAnswers.length]);

  const selection = useMemo(() => {
    if (!context) return null;
    return buildSelection(context, selectedWrongIndex);
  }, [context, selectedWrongIndex]);

  useEffect(() => {
    if (!isOpen) return;

    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key !== 'Escape') return;
      event.preventDefault();
      onClose();
    };

    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [isOpen, onClose]);

  if (!isOpen) {
    return null;
  }

  return (
    <div className="fixed inset-0 z-[1200] bg-black/55 backdrop-blur-sm p-2 sm:p-5" onClick={onClose}>
      <button
        type="button"
        onClick={(event) => {
          event.stopPropagation();
          onClose();
        }}
        className="absolute right-3 top-3 z-[60] rounded-full bg-white/95 p-2 text-[color:var(--gray-700)] shadow-md hover:bg-white sm:right-5 sm:top-5"
        aria-label="Close live tutor"
      >
        <X className="h-5 w-5" />
      </button>

      <div className="relative flex h-full w-full items-center justify-center overflow-auto" onClick={(event) => event.stopPropagation()}>
        <div className="flex h-full w-full max-w-[1500px] flex-col">
          {wrongAnswers.length > 1 && (
            <div className="mx-auto mb-3 mt-2 w-full max-w-[1200px] rounded-xl border border-[color:var(--surface-3)] bg-white/95 px-3 py-2">
              <div className="mb-2 text-xs font-medium text-[color:var(--gray-600)]">
                Choose a wrong question to review
              </div>
              <div className="flex flex-wrap gap-2">
                {wrongAnswers.map((answer, index) => {
                  const qn = answer.questionNumber ?? index + 1;
                  const batchNumber = (answer as { batchNumber?: number }).batchNumber;
                  const selected = index === selectedWrongIndex;
                  return (
                    <button
                      key={`${qn}-${index}`}
                      type="button"
                      onClick={() => setSelectedWrongIndex(index)}
                      className={`rounded-full border px-3 py-1.5 text-xs font-medium transition ${
                        selected
                          ? 'border-[color:var(--primary-500)] bg-[color:var(--primary-50)] text-[color:var(--primary-700)]'
                          : 'border-[color:var(--surface-3)] bg-white text-[color:var(--gray-600)] hover:border-[color:var(--primary-300)] hover:text-[color:var(--primary-600)]'
                      }`}
                    >
                      {batchNumber ? `Batch ${batchNumber} · Q${qn}` : `Question ${qn}`}
                    </button>
                  );
                })}
              </div>
            </div>
          )}

          <div className="min-h-0 flex-1">
            <LiveWhiteboard selection={selection} />
          </div>
        </div>
      </div>
    </div>
  );
}
